<script setup lang="ts">
import { useForm } from '@inertiajs/vue3'
import { Loader2, MessageSquare, Pin } from 'lucide-vue-next'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import CommentItem, { type Comment } from '@/components/comments/CommentItem.vue'
import LexicalEditor from '@/components/comments/LexicalEditor.vue'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'

const props = withDefaults(defineProps<{
  comments: Comment[]
  hasMore?: boolean
  loadMorePath?: string
  createPath: string
  canComment: boolean
}>(), {
  hasMore: false,
  loadMorePath: '',
})

type Filter = 'all' | 'activity' | 'comment'
const filter = ref<Filter>('all')

// ── Local state ───────────────────────────────────────────────────────────────
const localComments = ref<Comment[]>([...props.comments])
const localHasMore  = ref(props.hasMore)
const page          = ref(1)
const loading       = ref(false)

// Re-sync when deferred prop arrives (first load)
watch(() => props.comments, (val) => {
  localComments.value = [...val]
  localHasMore.value  = props.hasMore
  page.value          = 1
}, { deep: false })

// ── Derived ───────────────────────────────────────────────────────────────────
const pinned = computed(() => localComments.value.find(c => c.pinned) ?? null)

const feed = computed(() => {
  const unpinned = localComments.value.filter(c => !c.pinned)
  switch (filter.value) {
    case 'activity': return unpinned.filter(c => c.comment_type === 'activity')
    case 'comment':  return unpinned.filter(c => c.comment_type === 'comment')
    default:         return unpinned
  }
})

// ── Load more ─────────────────────────────────────────────────────────────────
async function loadMore() {
  if (loading.value || !localHasMore.value || !props.loadMorePath) return
  loading.value = true
  page.value++

  try {
    const url = new URL(props.loadMorePath, window.location.origin)
    url.searchParams.set('page', String(page.value))

    const res = await fetch(url.toString(), {
      headers:     { Accept: 'application/json' },
      credentials: 'same-origin',
    })

    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const json = await res.json()
    localComments.value.push(...json.comments)
    localHasMore.value = json.has_more
  } catch {
    page.value-- // roll back so next attempt retries the same page
  } finally {
    loading.value = false
  }
}

// ── IntersectionObserver sentinel ─────────────────────────────────────────────
const sentinel = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => { if (entries[0].isIntersecting) loadMore() },
    { rootMargin: '120px' },
  )
  if (sentinel.value) observer.observe(sentinel.value)
})

onUnmounted(() => observer?.disconnect())

// ── New comment form ──────────────────────────────────────────────────────────
const form = useForm({ comment: { body: null as string | null } })
const editorRef = ref<{ clear: () => void } | null>(null)

function submit() {
  if (!form.comment.body) return
  form.post(props.createPath, {
    preserveScroll: true,
    onSuccess: () => {
      editorRef.value?.clear()
      form.reset()
    },
  })
}
</script>

<template>
  <div class="mt-8 space-y-4">
    <Separator />

    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <MessageSquare class="text-muted-foreground size-4" />
        <h3 class="text-sm font-semibold">Activity & Comments</h3>
        <span class="text-muted-foreground text-xs">({{ localComments.length }}{{ localHasMore ? '+' : '' }})</span>
      </div>

      <!-- Filter toggle -->
      <div class="flex items-center gap-1 rounded-md border p-0.5 text-xs">
        <button
          v-for="opt in (['all', 'activity', 'comment'] as Filter[])"
          :key="opt"
          class="rounded px-2 py-0.5 capitalize transition-colors"
          :class="filter === opt ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'"
          @click="filter = opt"
        >
          {{ opt }}
        </button>
      </div>
    </div>

    <!-- New comment input -->
    <div v-if="canComment" class="space-y-2 pt-2">
      <LexicalEditor ref="editorRef" v-model="form.comment.body" />
      <div class="flex items-center justify-between">
        <p v-if="form.errors['comment.body']" class="text-destructive text-xs">{{ form.errors['comment.body'] }}</p>
        <div v-else />
        <Button
          size="sm"
          :disabled="form.processing || !form.comment.body"
          @click="submit"
        >
          Comment
        </Button>
      </div>
    </div>

    <!-- Pinned comment banner -->
    <div
      v-if="pinned && (filter === 'all' || filter === 'comment')"
      class="bg-muted/50 border-primary/20 flex items-start gap-3 rounded-lg border p-3"
    >
      <Pin class="text-primary mt-0.5 size-4 shrink-0" />
      <div class="min-w-0 flex-1">
        <p class="text-muted-foreground mb-0.5 text-xs font-medium uppercase tracking-wide">Pinned</p>
        <CommentItem :comment="pinned" />
      </div>
    </div>

    <!-- Comment list -->
    <div v-if="feed.length > 0" class="divide-border divide-y">
      <CommentItem v-for="c in feed" :key="c.id" :comment="c" />
    </div>
    <p v-else class="text-muted-foreground py-4 text-center text-sm">
      No {{ filter === 'all' ? 'activity or comments' : filter }} yet.
    </p>

    <!-- Infinite scroll sentinel -->
    <div ref="sentinel" class="flex justify-center py-2">
      <Loader2 v-if="loading" class="text-muted-foreground size-4 animate-spin" />
    </div>
  </div>
</template>
